import React from 'react';
import { Form, Label } from '@doveit/lego';
import merge from 'lodash.merge';
import FormAddressAutoComplete from '../form-address-autocomplete/FormAddressAutoComplete';
import EvaluationFormSchema from './EvaluationForm.schema';
import {
  propertyTypesRadio,
  floorSelectOptions,
  statusSelectOptions,
  featuresCheckboxes,
  PROPERTY_TYPE_APPARTAMENTO,
  PROPERTY_FEATURES_BALCONE_TERRAZZO,
  PROPERTY_FEATURES_GIARDINO,
  PROPERTY_FEATURES_BOX_AUTO,
  PROPERTY_FEATURES_ASCENSORE,
  PROPERTY_STATUS_ABITABILE,
  PROPERTY_TYPE_VILLA,
} from './fieldPresets';

import { defaultFieldsConfig } from './longFormFields';
import { FormFieldsConfig } from '../../types';

export const evaluationFormLevel = {
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
};

export interface EvaluationFormModel {
  type?: string,
  address?: string,
  floor?: string,
  size?: string,
  placeId?: string,
  status?: string,
  features?: {
    [PROPERTY_FEATURES_BALCONE_TERRAZZO]?: boolean,
    [PROPERTY_FEATURES_BOX_AUTO]?: boolean,
    [PROPERTY_FEATURES_GIARDINO]?: boolean,
    [PROPERTY_FEATURES_ASCENSORE]?: boolean,
  },
}

export interface EvaluationFormProps {
  onSubmit: (values: EvaluationFormModel) => void,
  initialValues?: EvaluationFormModel,
  fields?: FormFieldsConfig,
  loading?: boolean,
  dataTracking?: string,
}

const EvaluationForm: React.FC<EvaluationFormProps> = ({
  onSubmit,
  initialValues = {},
  fields,
  loading,
  dataTracking,
}) => {
  const selectedFields = fields || defaultFieldsConfig;

  const initialValuesMerged = merge({}, {
    type: PROPERTY_TYPE_APPARTAMENTO,
    status: PROPERTY_STATUS_ABITABILE,
    address: '',
    placeId: null,
    floor: '',
    size: '',
    features: {
      [PROPERTY_FEATURES_BALCONE_TERRAZZO]: false,
      [PROPERTY_FEATURES_BOX_AUTO]: false,
      [PROPERTY_FEATURES_GIARDINO]: false,
      [PROPERTY_FEATURES_ASCENSORE]: false,
    },
  }, initialValues);

  const internalOnSubmit = React.useCallback(
    (values) => {
      const normalizedValues = values;

      if (values.type === PROPERTY_TYPE_VILLA) {
        normalizedValues.floor = undefined;
        normalizedValues.features[PROPERTY_FEATURES_BALCONE_TERRAZZO] = false;
        normalizedValues.features[PROPERTY_FEATURES_ASCENSORE] = false;
      }

      onSubmit(normalizedValues);
    }, [onSubmit],
  );

  return (
    <Form
      initialValues={initialValuesMerged}
      onSubmit={internalOnSubmit}
      validationSchema={EvaluationFormSchema}
      loading={loading}
      preventEnterKeypress
    >
      {({
        values,
        setFieldValue,
      }) => {
        const onSuggestionSelected = ({ placeId }) => setFieldValue('placeId', placeId);
        const onSuggestionChange = () => setFieldValue('placeId', null);

        return (
          <>
            {selectedFields.type && (
              <Form.RadioGroup
                name="type"
                label={selectedFields.type.label}
              >
                {propertyTypesRadio.map(({ value, label }) => (
                  <Form.Item
                    key={`evaluation-form-property-type-${value}`}
                    size={{ XS: 1 / 2 }}
                  >
                    <Form.RadioButton
                      name="type"
                      aria-label={selectedFields.type.ariaLabel}
                      text={label}
                      value={value}
                    />
                  </Form.Item>
                ))}
              </Form.RadioGroup>
            )}
            {selectedFields.address && (
              <Form.Item>
                <FormAddressAutoComplete
                  id="lead-address"
                  name="address"
                  label={selectedFields.address.label}
                  aria-label={selectedFields.address.label}
                  placeholder={selectedFields.address.placeholder}
                  onChange={onSuggestionChange}
                  onSuggestionSelected={onSuggestionSelected}
                  required
                />
              </Form.Item>
            )}
            {selectedFields.floor && values.type === PROPERTY_TYPE_APPARTAMENTO && (
              <Form.Item>
                <Form.Select
                  name="floor"
                  label={selectedFields.floor.label}
                  aria-label={selectedFields.floor.ariaLabel}
                  options={floorSelectOptions}
                  required
                />
              </Form.Item>
            )}
            {selectedFields.size && (
              <Form.Item>
                <Form.Input
                  type="number"
                  name="size"
                  min="0"
                  placeholder={selectedFields.size.placeholder}
                  label={selectedFields.size.label}
                  aria-label={selectedFields.size.ariaLabel}
                  pattern="\d*"
                  required
                />
              </Form.Item>
            )}
            {selectedFields.status && (
              <Form.Item>
                <Form.Select
                  name="status"
                  label={selectedFields.status.label}
                  aria-label={selectedFields.status.ariaLabel}
                  options={statusSelectOptions}
                  required
                />
              </Form.Item>
            )}

            {selectedFields.features && (
              <>
                <Form.Item>
                  <Label text={selectedFields.features.label} />
                </Form.Item>
                <Form.Group>
                  {featuresCheckboxes.map(({ type, label }) => (
                    (values.type === PROPERTY_TYPE_APPARTAMENTO || (type === PROPERTY_FEATURES_GIARDINO || type === PROPERTY_FEATURES_BOX_AUTO)) && (
                      <Form.Item
                        key={type}
                        size={{ MD: 1 / 2 }}
                        compact
                      >
                        <Form.CheckboxButton
                          name={`features.${type}`}
                          text={label}
                          aria-label={label}
                          dataRef={`evaluation-form-features-${type}`}
                        />
                      </Form.Item>
                    )
                  ))}
                </Form.Group>
              </>
            )}
            <Form.Item>
              <Form.Submit
                label={selectedFields.button.label}
                aria-label={selectedFields.button.ariaLabel}
                dataTracking={dataTracking}
                expanded
              />
            </Form.Item>
          </>
        );
      }}
    </Form>
  );
};

export default EvaluationForm;
